import { useState, useEffect } from "react";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Send, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCTION_URL = "https://apoyum.com";

export default function AdminInvitations() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "" });

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      const response = await axios.get(`${API}/admin/invitations`);
      setInvitations(response.data);
    } catch (error) {
      console.error("Error fetching invitations:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendInvitation = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      const response = await axios.post(`${API}/admin/invite-listener`, formData);
      await fetchInvitations();
      setFormData({ name: "", email: "" });
      toast.success("Invitación enviada");
      
      // Copy link to clipboard
      const link = `${PRODUCTION_URL}/registro-listener?token=${response.data.token}`;
      navigator.clipboard.writeText(link);
      toast.info("Enlace copiado al portapapeles");
    } catch (error) {
      console.error("Error sending invitation:", error);
      toast.error(error.response?.data?.detail || "Error al enviar la invitación");
    } finally {
      setSending(false);
    }
  };

  const copyInviteLink = (token) => {
    const link = `${PRODUCTION_URL}/registro-listener?token=${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Enlace copiado");
  };

  return (
    <AdminLayout title="Invitaciones">
      <div className="space-y-8">
        {/* Invite Form */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Invitar nuevo listener</h2>
          <form onSubmit={sendInvitation} className="flex items-end gap-4">
            <div className="flex-1">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre del listener"
                required
                className="mt-2"
                data-testid="invite-name"
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@ejemplo.com"
                required
                className="mt-2"
                data-testid="invite-email"
              />
            </div>
            <Button type="submit" className="btn-primary" disabled={sending} data-testid="send-invite-btn">
              <Send className="w-4 h-4 mr-2" />
              {sending ? "Enviando..." : "Enviar invitación"}
            </Button>
          </form>
        </div>

        {/* Invitations List */}
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className="p-4 border-b border-stone-200">
            <h2 className="text-lg font-semibold text-foreground">Invitaciones enviadas</h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-stone-600">No hay invitaciones</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((invitation) => (
                  <TableRow key={invitation.id} data-testid={`invitation-row-${invitation.id}`}>
                    <TableCell className="font-medium">{invitation.name}</TableCell>
                    <TableCell>{invitation.email}</TableCell>
                    <TableCell>
                      {format(parseISO(invitation.created_at), "d MMM yyyy", { locale: es })}
                    </TableCell>
                    <TableCell>
                      {invitation.used ? (
                        <span className="badge-approved">Usada</span>
                      ) : new Date(invitation.expires_at) < new Date() ? (
                        <span className="badge-rejected">Expirada</span>
                      ) : (
                        <span className="badge-pending">Pendiente</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!invitation.used && new Date(invitation.expires_at) > new Date() && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => copyInviteLink(invitation.token)}
                          data-testid={`copy-invite-${invitation.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
